import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { redefinirCodigoAcesso, solicitarRecuperacaoCodigo } from '@/api/usuariosApi'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/contexts/ToastContext'
import { markClienteSessionActive } from '@/utils/clienteAuth'
import { getClientLoginPath } from '@/config/tenant'
import { TenantIdentityCard } from '@/components/TenantIdentity'
import styles from './RecuperarCodigoPage.module.css'

export function RecuperarCodigoPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tokenFromUrl = searchParams.get('token')?.trim() || ''
  const { setUser } = useUser()
  const { toast } = useToast()

  const [email, setEmail] = useState('')
  const [token, setToken] = useState(tokenFromUrl)
  const [codigoNovo, setCodigoNovo] = useState('')
  const [codigoConf, setCodigoConf] = useState('')
  const [loading, setLoading] = useState(false)
  const [pedidoEnviado, setPedidoEnviado] = useState(false)

  useEffect(() => {
    if (tokenFromUrl) setToken(tokenFromUrl)
  }, [tokenFromUrl])

  const modoRedefinir = token.length > 10

  const handleSolicitar = async (e: React.FormEvent) => {
    e.preventDefault()
    const emailTrim = email.trim()
    if (!emailTrim) {
      toast('Indique o seu e-mail.', 'error')
      return
    }
    setLoading(true)
    try {
      const res = await solicitarRecuperacaoCodigo(emailTrim)
      setPedidoEnviado(true)
      toast(res.message, 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao enviar pedido.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleRedefinir = async (e: React.FormEvent) => {
    e.preventDefault()
    if (codigoNovo.length < 4) {
      toast('O novo código deve ter pelo menos 4 caracteres.', 'error')
      return
    }
    if (codigoNovo !== codigoConf) {
      toast('A confirmação do código não coincide.', 'error')
      return
    }
    setLoading(true)
    try {
      const res = await redefinirCodigoAcesso(token, codigoNovo)
      markClienteSessionActive()
      setUser({
        id: res.id,
        nome: res.nome,
        telefone: res.telefone,
        email: res.email,
        avatar_url: res.avatar_url ?? null,
      })
      toast('Código actualizado. Já pode usar a app.', 'success')
      navigate('/menu', { replace: true })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao redefinir código.', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className={`standalone-page ${styles.page}`}>
      <TenantIdentityCard className={styles.identity} />
      <div className={styles.card}>
        <h1 className={styles.title}>
          {modoRedefinir ? 'Definir novo código' : 'Esqueci o acesso'}
        </h1>
        <p className={styles.subtitle}>
          {modoRedefinir
            ? 'Escolha um novo código de acesso (mínimo 4 caracteres).'
            : 'Indique o e-mail da sua conta. Se existir, receberá um link para redefinir o código.'}
        </p>

        {modoRedefinir ? (
          <form className={styles.form} onSubmit={handleRedefinir}>
            {!tokenFromUrl && (
              <label className={styles.label}>
                Código do e-mail
                <input
                  className={styles.input}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Cole o código do link"
                />
              </label>
            )}
            <label className={styles.label}>
              Novo código de acesso
              <input
                type="password"
                className={styles.input}
                value={codigoNovo}
                onChange={(e) => setCodigoNovo(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label className={styles.label}>
              Confirmar novo código
              <input
                type="password"
                className={styles.input}
                value={codigoConf}
                onChange={(e) => setCodigoConf(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              {loading ? 'A guardar…' : 'Guardar e entrar'}
            </button>
          </form>
        ) : pedidoEnviado ? (
          <p className={styles.hint}>
            Se existir uma conta com esse e-mail, receberá instruções em breve. Verifique também a pasta de spam.
          </p>
        ) : (
          <form className={styles.form} onSubmit={handleSolicitar}>
            <label className={styles.label}>
              E-mail da conta
              <input
                type="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              {loading ? 'A enviar…' : 'Enviar link de recuperação'}
            </button>
          </form>
        )}

        <p className={styles.footer}>
          <Link to={getClientLoginPath()}>Voltar ao login</Link>
        </p>
      </div>
    </main>
  )
}
